import React, { useEffect, useState } from "react";
import classes from "./ChangePassword.module.css";
import API from "../../utils/api";
import RenderToast from "../../components/RenderToast";
import { FiEye, FiEyeOff } from "react-icons/fi";

export default function ChangePasswordModal({
  open,
  onClose,
  userId,
  onSuccess,
}) {
  const [form, setForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState({
    old: false,
    new: false,
    confirm: false,
  });

  useEffect(() => {
    if (!open || !userId) return;
    let cancelled = false;
    const fetchPassword = async () => {
      try {
        const token = localStorage.getItem("authToken") || "";
        if (!token) return;
        const formData = new URLSearchParams();
        formData.append("action", "fetch");
        formData.append("user_id", userId);
        const response = await fetch(`${API.base}/profile.php`, {
          method: "POST",
          headers: {
            Authorization: token,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: formData,
        });
        const data = await response.json();
        if (
          !cancelled &&
          data.success &&
          data.user &&
          data.user.password_plain
        ) {
          setForm((prev) => ({
            ...prev,
            oldPassword: data.user.password_plain,
          }));
        }
      } catch (err) {
        console.error("Failed to prefill password", err);
      }
    };
    fetchPassword();
    return () => {
      cancelled = true;
    };
  }, [open, userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.oldPassword || !form.newPassword || !form.confirmPassword) {
      return RenderToast({
        type: "error",
        message: "All password fields are required",
      });
    }
    if (form.newPassword !== form.confirmPassword) {
      return RenderToast({
        type: "error",
        message: "New password and confirm password must match",
      });
    }
    if (form.newPassword.length < 6) {
      return RenderToast({
        type: "error",
        message: "Password must be at least 6 characters",
      });
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("action", "change_password");
      formData.append("user_id", userId);
      formData.append("old_password", form.oldPassword);
      formData.append("new_password", form.newPassword);

      const token = localStorage.getItem("authToken") || "";
      const response = await fetch(`${API.base}/profile.php`, {
        method: "POST",
        headers: {
          Authorization: token,
        },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to update password");
      }
      RenderToast({ type: "success", message: "Password updated successfully" });
      setForm({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      RenderToast({ type: "error", message: err.message });
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className={classes.backdrop} onClick={onClose}>
      <div
        className={classes.modal}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <header className={classes.header}>
          <h3>Change Password</h3>
          <button className={classes.closeBtn} onClick={onClose}>
            ×
          </button>
        </header>

        <form onSubmit={handleSubmit} className={classes.form}>
          <label>
            <span>Old Password</span>
            <div className={classes.passwordField}>
              <input
                type={showPassword.old ? "text" : "password"}
                name="oldPassword"
                value={form.oldPassword}
                onChange={handleChange}
                placeholder="Enter current password"
              />
              <button
                type="button"
                className={classes.eyeBtn}
                onClick={() => togglePasswordVisibility("old")}
                aria-label={
                  showPassword.old ? "Hide current password" : "Show current password"
                }
              >
                {showPassword.old ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
          </label>
          <label>
            <span>New Password</span>
            <div className={classes.passwordField}>
              <input
                type={showPassword.new ? "text" : "password"}
                name="newPassword"
                value={form.newPassword}
                onChange={handleChange}
                placeholder="Enter new password"
              />
              <button
                type="button"
                className={classes.eyeBtn}
                onClick={() => togglePasswordVisibility("new")}
                aria-label={
                  showPassword.new ? "Hide new password" : "Show new password"
                }
              >
                {showPassword.new ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
          </label>
          <label>
            <span>Confirm Password</span>
            <div className={classes.passwordField}>
              <input
                type={showPassword.confirm ? "text" : "password"}
                name="confirmPassword"
                value={form.confirmPassword}
                onChange={handleChange}
                placeholder="Re-enter new password"
              />
              <button
                type="button"
                className={classes.eyeBtn}
                onClick={() => togglePasswordVisibility("confirm")}
                aria-label={
                  showPassword.confirm ? "Hide confirm password" : "Show confirm password"
                }
              >
                {showPassword.confirm ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
          </label>

          <div className={classes.actions}>
            <button
              type="button"
              className={classes.secondaryBtn}
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={classes.primaryBtn}
              disabled={loading}
            >
              {loading ? "Updating..." : "Update Password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

